import React from "react";
import Button from "../../Button";
import { twClassNames } from "../../../helpers/twClassNames.js";
import { FeatureComparison } from "./FeatureComparisonCredits";
import { getPlanData } from "../../../../../clientServices/workspaceFeaturesCredits";
import { TooltipContainer } from "../../Tooltip.jsx";
import ToggleSwitch from "../../ToggleSwitch";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import { Plan } from "@functions/plans/types";
import { getPricingTablePlans } from "@functions/plans/utilities";

const rowHighlightCls =
    "border-1 rounded-lg !border-yellow-300 bg-gradient-to-b from-white to-yellow-100 font-medium !text-yellow-700 mt-2";

const ComparisonTable = ({
    currentPlan,
    frequency,
    excludePublic = false,
    handleSalesFormClick = null,
    getPlanButton,
    fsrc = "",
    onFrequencyChange
}) => {
    const projectSelector = useProjectSelector();
    const plansOnTable = getPricingTablePlans(currentPlan);
    const planData = getPlanData(currentPlan, frequency, plansOnTable as any, projectSelector);
    const planDataExcludingPublic = getPlanData(
        currentPlan,
        frequency,
        getPricingTablePlans(currentPlan, [Plan.Public]) as any,
        projectSelector
    );
    return (
        <div className="comparisonTable">
            <div className="hidden w-full lg:flex lg:flex-col lg:gap-6">
                <PlanHeaders
                    planData={excludePublic ? planDataExcludingPublic : planData}
                    getPlanButton={getPlanButton}
                    frequency={frequency}
                    onFrequencyChange={onFrequencyChange}
                />
                <FeatureComparison
                    planData={excludePublic ? planDataExcludingPublic : planData}
                    fsrc={fsrc}
                />
            </div>
            <div className="w-full lg:hidden">
                <PlanHeaders
                    planData={planDataExcludingPublic}
                    getPlanButton={getPlanButton}
                    frequency={frequency}
                    onFrequencyChange={onFrequencyChange}
                />
                <FeatureComparison planData={planDataExcludingPublic} fsrc={fsrc} />
            </div>
            {handleSalesFormClick && (
                <div className="talktoSales">
                    Read everything and still have questions?
                    <Button type="secondary" onClick={handleSalesFormClick}>
                        Talk to Sales
                    </Button>
                </div>
            )}
        </div>
    );
};

const PlanHeaders = ({ planData, getPlanButton, frequency, onFrequencyChange }) => {
    return (
        <div className="planHeaders">
            <div className="planHeadersBg"></div>
            {Object.entries(planData).map(([key, plan]) => (
                <ComparisonTable.PlanHeader
                    key={key}
                    planKey={key}
                    planDetails={plan}
                    getPlanButton={getPlanButton}
                    frequency={frequency}
                    onFrequencyChange={onFrequencyChange}
                />
            ))}
        </div>
    );
};

ComparisonTable.PlanHeader = ({
    planKey,
    planDetails,
    getPlanButton,
    frequency,
    onFrequencyChange
}) => (
    <div className="planHeader">
        <div className="headerTitle">
            <i
                className={twClassNames(
                    planDetails.icon,
                    planDetails.iconColor || "text-purple-600"
                )}
            ></i>
            <h3 className="title">{planDetails.name}</h3>
        </div>
        <div className="w-full">
            <div className="flex flex-row items-center gap-1">
                <p className="price m-0 p-0 text-left">
                    {planDetails.price}
                    {planDetails.condensedPriceDetails}
                </p>
                {/* TODO: Centralize in `@functions/plans` or `@functions/featureAvailability` */}
                {[Plan.UbpBasic, Plan.UbpCore, Plan.UbpGrowth].includes(planKey as any) && (
                    <div className="flex flex-wrap items-center gap-1 text-sm">
                        <ToggleSwitch
                            enabled={frequency.interval === "annually"}
                            onChange={(enabled) => {
                                onFrequencyChange(enabled ? "annually" : "monthly");
                            }}
                            size="small"
                            classNames="mx-1"
                            value={frequency.interval}
                            aria-label="Toggle annual/monthly pricing"
                        />
                        <span
                            className={
                                frequency.interval === "annually"
                                    ? "font-semibold text-brand"
                                    : "text-gray-500"
                            }
                        >
                            Billed annually
                        </span>
                    </div>
                )}
            </div>
        </div>
        {getPlanButton({ planKey, planDetails })}
    </div>
);

ComparisonTable.SectionHeader = ({ text }) => <h4 className="sectionHeader">{text}</h4>;

ComparisonTable.SubsectionHeader = ({ text, highlight = false }) => (
    <h4 className={twClassNames("subsectionHeader", highlight && rowHighlightCls)}>{text}</h4>
);

ComparisonTable.RowCheckmark = ({
    name,
    values,
    explainerText = "",
    noBorder = false,
    highlight = false,
    link = null
}) => (
    <div
        className={twClassNames(
            "rowCheckmark",
            { hasBorder: !noBorder },
            highlight && rowHighlightCls
        )}
    >
        <span className="explainer">
            {link ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                >
                    {name}
                </a>
            ) : (
                name
            )}
            {explainerText && explainerText.length > 0 && (
                <TooltipContainer type={highlight ? "yellow" : "secondary"} body={explainerText} />
            )}
        </span>
        {values.map((value, index) => (
            <ComparisonTable.Cell key={index}>
                {value && (
                    <i
                        className={twClassNames(
                            "fas text-lg",
                            highlight
                                ? "fa-circle-check text-yellow-700"
                                : "fa-check text-green-500"
                        )}
                    ></i>
                )}
            </ComparisonTable.Cell>
        ))}
    </div>
);

ComparisonTable.RowCustom = ({
    name,
    children,
    explainerText = "",
    noBorder = false,
    highlight = false,
    link = null
}) => (
    <div
        className={twClassNames(
            "rowCustom",
            { hasBorder: !noBorder },
            highlight && rowHighlightCls
        )}
    >
        <span className="explainer">
            {link ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                >
                    {name}
                </a>
            ) : (
                name
            )}
            {explainerText && explainerText.length > 0 && (
                <TooltipContainer type={highlight ? "yellow" : "secondary"} body={explainerText} />
            )}
        </span>
        {children}
    </div>
);

ComparisonTable.Cell = ({ children }) => <div className="flex-1 text-center">{children}</div>;

export default ComparisonTable;
